import React, { useState, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import AkIcon from '@atlaskit/icon';
import { Box, Text } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { InProductHelpLink } from '@atlassian/jira-servicedesk-common/src/ui/components/in-product-help-link';
import { AlertsGlyph } from '@atlassian/jira-servicedesk-incident-management-common/src/assets/index.tsx';
import { AsyncEditPriorityMappingModal } from '@atlassian/jira-servicedesk-incident-management-priority-mapping-modal/src/async';
import { SettingsCardComponent } from '@atlassian/jira-servicedesk-settings-common/src/ui/settings-card-component';
import { toProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import {
	settingsCardComponentStyles,
	sectionParagraphStyles,
	fieldDescriptionStyles,
} from '../../common/ui';
import { SectionTitle } from '../../common/ui/section-title';
import { useShouldSendResponderAlerts } from '../../services/main.tsx';
import messages from './messages';

export const ToggleResponderAlertsSection = () => {
	const { formatMessage } = useIntl();
	const { cloudId } = useTenantContext();
	const { data: projectContext } = useProjectContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isPrioritiesModalOpen, setIsPrioritiesModalOpen] = useState(false);
	const projectId = projectContext ? toProjectId(projectContext.projectId.toString()) : undefined;

	const [{ shouldSendResponderAlerts }, { setResponderAlertsConfiguration }] =
		useShouldSendResponderAlerts();

	const onShouldSendResponderAlertsChange = (
		event: SyntheticEvent<HTMLInputElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		const newValue = !shouldSendResponderAlerts;
		fireUIAnalytics(analyticsEvent, 'toggle clicked', 'toggleResponderAlerts', {
			value: newValue,
		});
		setResponderAlertsConfiguration({
			cloudId,
			projectId,
			shouldSendResponderAlerts: newValue,
			analyticsEvent,
		});
	};

	const onLearnMoreLinkClick = (articleId: string) => {
		fireUIAnalytics(
			createAnalyticsEvent({}),
			'helpLink clicked',
			'toggleResponderAlertsDescriptionReadMoreLink',
			{ articleId },
		);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onOpenEditAlertPriorityMappingModalClick = (_: any, analyticsEvent: any) => {
		setIsPrioritiesModalOpen(true);
		fireUIAnalytics(analyticsEvent, 'button clicked', 'openEditAlertPriorityMappingModal');
	};

	const learnMoreAboutResponderAlertArticleId = '1WrMEi4Eisneb4mYS5rhcN';
	const learnMoreAboutAlertPriorityArticleId = '5q5eXHA8UmxsJo0gffKR4a';

	return (
		<>
			{isPrioritiesModalOpen && (
				<AsyncEditPriorityMappingModal
					cloudId={cloudId}
					onClose={() => setIsPrioritiesModalOpen(false)}
				/>
			)}
			<SettingsCardComponent
				title={
					<SectionTitle
						// @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'.
						icon={<AkIcon glyph={AlertsGlyph} label="" />}
						message={formatMessage(messages.alertingSectionHeading)}
					/>
				}
				styles={settingsCardComponentStyles}
				footerActionItems={[
					<Button
						key="editAlertPriorityButton"
						appearance="default"
						onClick={onOpenEditAlertPriorityMappingModalClick}
					>
						{formatMessage(messages.assignAlertPriorityButtonText)}
					</Button>,
				]}
			>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
				<Box xcss={[sectionParagraphStyles, fieldDescriptionStyles]}>
					{ff('agni-replace-typography-css-with-tokens_cgl2g') ? (
						<Text>{formatMessage(messages.alertingSectionDescription)}</Text>
					) : (
						<>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<span>{formatMessage(messages.alertingSectionDescription)}</span>&nbsp;
						</>
					)}
				</Box>
				<InProductHelpLink
					href="https://support.atlassian.com/jira-service-management-cloud/docs/what-are-responder-alerts/"
					onClick={() => onLearnMoreLinkClick(learnMoreAboutResponderAlertArticleId)}
					learnMoreText={formatMessage(messages.responderAlertLearnMoreLink)}
					inProductHelpArticleId={learnMoreAboutResponderAlertArticleId}
				/>
				<ToggleContainer>
					<Toggle
						testId="servicedesk-incident-management-settings.ui.responder-alerts.send-responder-alerts-toggle"
						isChecked={shouldSendResponderAlerts}
						onChange={onShouldSendResponderAlertsChange}
						label={formatMessage(messages.alertingSectionToggleLabel)}
					/>
					<ToggleDescription>
						{formatMessage(messages.alertingSectionToggleLabel)}
					</ToggleDescription>
				</ToggleContainer>
				<h5>{formatMessage(messages.manageAlertPriorityHeading)}</h5>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
				<Box xcss={[sectionParagraphStyles, fieldDescriptionStyles]}>
					{formatMessage(messages.manageAlertPriorityDescription)}
				</Box>
				<InProductHelpLink
					href="https://support.atlassian.com/jira-service-management-cloud/docs/what-are-alert-priorities/"
					onClick={() => onLearnMoreLinkClick(learnMoreAboutAlertPriorityArticleId)}
					learnMoreText={formatMessage(messages.manageAlertPriorityLearnMoreLink)}
					inProductHelpArticleId={learnMoreAboutAlertPriorityArticleId}
				/>
			</SettingsCardComponent>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToggleContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	margin: `${gridSize * 1.25}px 0 ${token('space.300', '24px')} ${-gridSize / 2 + 1}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToggleDescription = styled.span({
	display: 'inline-block',
	marginLeft: token('space.025', '2px'),
});
